/**
 * 小红书爆款 - 情感故事模板
 *
 * 视觉结构：
 * - 开场钩子（3秒）：情感化问题 + 呼吸动画 + 浮动爱心
 * - 主体内容（15秒）：故事卡片 + 色彩标记条 + 渐入
 * - 结尾CTA（2秒）：鼓励文字 + 涟漪CTA
 *
 * 视觉元素：
 * - 配色：柔和温暖（玫瑰粉 / 蜜桃橙 / 薰衣草紫）
 * - 字号：100-120px（开场）、36-48px（故事）
 * - 动画：柔缓呼吸、浮动粒子、丝滑入场
 * - 背景：温暖光晕 + 浮动爱心粒子
 */

import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  Sequence,
} from 'remotion';

const EMOTIONAL_COLORS = {
  primary: {
    rose: '#F472B6',
    peach: '#FB923C',
    coral: '#F87171',
    blush: '#FDA4AF',
  },
  secondary: {
    lavender: '#C084FC',
    cream: '#FDE68A',
    lightPink: '#FBCFE8',
    gold: '#FBBF24',
    sky: '#7DD3FC',
  },
  gradients: {
    warm: 'linear-gradient(160deg, #831843 0%, #9F1239 30%, #C2410C 70%, #92400E 100%)',
    warmOverlay: 'linear-gradient(160deg, #F472B6 0%, #FB923C 50%, #FDE68A 100%)',
    love: 'linear-gradient(135deg, #F472B6 0%, #C084FC 100%)',
    sunset: 'linear-gradient(180deg, #FB923C 0%, #F87171 100%)',
    card: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.88) 100%)',
  },
  white: '#FFFFFF',
  black: '#3E2723',
  gray: '#78716C',
  cream: '#FFF7ED',
};

// ============================================
// ✨ 温暖光晕 + 浮动爱心背景
// ============================================
const WarmGlowBackground: React.FC<{
  count?: number;
}> = ({ count = 30 }) => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();

  const glows = Array.from({ length: count }, (_, i) => {
    const x = (i * 47.137) % width;
    const baseY = (i * 39.723) % height;
    const floatY = Math.sin(frame * 0.018 + i * 0.7) * 40;
    const floatX = Math.cos(frame * 0.012 + i * 0.5) * 20;
    const size = 120 + (i % 5) * 60;
    const opacity = 0.1 + Math.sin(frame * 0.012 + i * 0.5) * 0.05;
    const colors = [
      EMOTIONAL_COLORS.primary.rose,
      EMOTIONAL_COLORS.primary.peach,
      EMOTIONAL_COLORS.primary.coral,
      EMOTIONAL_COLORS.secondary.lavender,
    ];
    return { x: x + floatX, y: baseY + floatY, size, opacity, color: colors[i % colors.length] };
  });

  const hearts = Array.from({ length: 12 }, (_, i) => {
    const x = (i * 87.3 + 50) % width;
    const speed = 0.6 + (i % 3) * 0.3;
    const y = height - ((frame * speed + i * 160) % (height + 200));
    const size = 16 + (i % 4) * 8;
    const opacity = 0.15 + Math.sin(frame * 0.03 + i) * 0.08;
    const rotate = Math.sin(frame * 0.04 + i * 2) * 15;
    return { x, y, size, opacity, rotate };
  });

  return (
    <AbsoluteFill style={{ overflow: 'hidden' }}>
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          background: EMOTIONAL_COLORS.gradients.warm,
        }}
      />

      {/* 光晕 */}
      {glows.map((glow, i) => (
        <div
          key={`g${i}`}
          style={{
            position: 'absolute',
            left: glow.x,
            top: glow.y,
            width: glow.size,
            height: glow.size,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${glow.color} 0%, transparent 70%)`,
            opacity: glow.opacity,
            filter: 'blur(50px)',
          }}
        />
      ))}

      {/* 浮动爱心 */}
      {hearts.map((h, i) => (
        <div
          key={`h${i}`}
          style={{
            position: 'absolute',
            left: h.x,
            top: h.y,
            fontSize: h.size,
            opacity: h.opacity,
            transform: `rotate(${h.rotate}deg)`,
            filter: 'blur(1px)',
          }}
        >
          ❤️
        </div>
      ))}
    </AbsoluteFill>
  );
};

// ============================================
// 🎬 开场钩子 - 情感化问题（3秒）
// ============================================
const EmotionalHookScene: React.FC<{
  question: string;
  subtext?: string;
}> = ({ question, subtext }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const textProgress = spring({
    frame,
    fps,
    config: { damping: 22, stiffness: 70 },
  });

  const textOpacity = interpolate(textProgress, [0, 1], [0, 1]);
  const textY = interpolate(textProgress, [0, 1], [-40, 0]);
  const breathe = 1 + Math.sin(frame * 0.08) * 0.02;

  const subtextProgress = spring({
    frame: frame - 25,
    fps,
    config: { damping: 22, stiffness: 70 },
  });

  const lineWidth = interpolate(textProgress, [0, 1], [0, 300]);

  return (
    <AbsoluteFill
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 64,
      }}
    >
      {/* 主问题 */}
      <div
        style={{
          fontSize: 110,
          fontWeight: 700,
          color: EMOTIONAL_COLORS.white,
          textAlign: 'center',
          lineHeight: 1.3,
          opacity: textOpacity,
          transform: `translateY(${textY}px) scale(${breathe})`,
          textShadow: '0 6px 40px rgba(0,0,0,0.35)',
          marginBottom: 32,
          letterSpacing: 1,
        }}
      >
        {question}
      </div>

      {/* 分隔线 */}
      <div
        style={{
          width: lineWidth,
          height: 3,
          background: EMOTIONAL_COLORS.gradients.warmOverlay,
          borderRadius: 2,
          marginBottom: 32,
          opacity: textOpacity,
        }}
      />

      {/* 副文字 */}
      {subtext && (
        <div
          style={{
            fontSize: 54,
            fontWeight: 600,
            color: 'rgba(255,255,255,0.85)',
            textAlign: 'center',
            opacity: interpolate(subtextProgress, [0, 1], [0, 1]),
            transform: `translateY(${interpolate(subtextProgress, [0, 1], [20, 0])}px)`,
            textShadow: '0 4px 16px rgba(0,0,0,0.2)',
            lineHeight: 1.5,
          }}
        >
          {subtext}
        </div>
      )}
    </AbsoluteFill>
  );
};

// ============================================
// 📖 故事卡片
// ============================================
const StoryCard: React.FC<{
  number: string;
  title: string;
  content: string;
  emoji?: string;
  delay: number;
  color: string;
}> = ({ number, title, content, emoji = '✨', delay, color }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const progress = spring({
    frame: frame - delay,
    fps,
    config: { damping: 18, stiffness: 90 },
  });

  const translateY = interpolate(progress, [0, 1], [60, 0]);
  const scale = interpolate(progress, [0, 1], [0.92, 1]);

  return (
    <div
      style={{
        background: EMOTIONAL_COLORS.gradients.card,
        backdropFilter: 'blur(24px)',
        borderRadius: 28,
        padding: '32px 36px',
        opacity: progress,
        transform: `translateY(${translateY}px) scale(${scale})`,
        boxShadow: '0 8px 40px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)',
        display: 'flex',
        gap: 24,
        overflow: 'hidden',
        position: 'relative',
        marginTop: 15
      }}
    >
      {/* 左侧色条 */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 6,
          background: color,
          borderRadius: '28px 0 0 28px',
        }}
      />

      {/* 编号+Emoji */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
          paddingLeft: 8,
        }}
      >
        <div
          style={{
            width: 70,
            height: 70,
            borderRadius: 16,
            background: `linear-gradient(135deg, ${color} 0%, ${color}CC 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 46,
            fontWeight: 800,
            color: EMOTIONAL_COLORS.white,
            boxShadow: `0 4px 16px ${color}40`,
          }}
        >
          {number}
        </div>
        <div style={{ fontSize: 40 }}>{emoji}</div>
      </div>

      {/* 文字内容 */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 48,
            fontWeight: 700,
            color: EMOTIONAL_COLORS.black,
            marginBottom: 12,
            lineHeight: 1.2,
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 38,
            fontWeight: 500,
            color: EMOTIONAL_COLORS.gray,
            lineHeight: 1.5,
          }}
        >
          {content}
        </div>
      </div>
    </div>
  );
};

// ============================================
// 🎬 主体故事场景（15秒）
// ============================================
const StoryScene: React.FC<{
  stories: Array<{
    number: string;
    title: string;
    content: string;
    emoji?: string;
  }>;
  sceneTitle?: string;
}> = ({ stories, sceneTitle }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleProgress = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 140 },
  });

  const accentColors = [
    EMOTIONAL_COLORS.primary.rose,
    EMOTIONAL_COLORS.primary.peach,
    EMOTIONAL_COLORS.secondary.lavender,
    EMOTIONAL_COLORS.primary.coral,
  ];

  return (
    <AbsoluteFill>
      <div
        style={{
          position: 'absolute',
          top: 90,
          left: 64,
          right: 64,
          textAlign: 'center',
        }}
      >
        <div
          style={{
            fontSize: 60,
            fontWeight: 700,
            color: EMOTIONAL_COLORS.white,
            opacity: titleProgress,
            transform: `translateY(${interpolate(titleProgress, [0, 1], [-24, 0])}px)`,
            textShadow: '0 4px 20px rgba(0,0,0,0.3)',
            letterSpacing: 1,
          }}
        >
          {sceneTitle || `💪 ${stories.length} 个真实故事`}
        </div>
      </div>

      <div
        style={{
          position: 'absolute',
          top: 240,
          left: 40,
          right: 40,
          display: 'flex',
          flexDirection: 'column',
          gap: 28,
        }}
      >
        {stories.map((story, i) => (
          <StoryCard
            key={i}
            {...story}
            delay={18 + i * 45}
            color={accentColors[i % accentColors.length]}
          />
        ))}
      </div>
    </AbsoluteFill>
  );
};

// ============================================
// 🎬 结尾CTA场景（2秒）
// ============================================
const EmotionalCTAScene: React.FC<{
  encouragement: string;
  ctaText: string;
}> = ({ encouragement, ctaText }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const progress = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 140 },
  });

  const scale = interpolate(progress, [0, 1], [0.6, 1]);
  const heartbeat = 1 + Math.sin(frame * 0.18) * 0.03;

  const rippleScale = 1 + (frame % 40) / 40 * 0.5;
  const rippleOpacity = 1 - (frame % 40) / 40;

  return (
    <AbsoluteFill
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {/* 涟漪效果 */}
      {[0, 12, 24].map((offset, i) => {
        const localFrame = (frame + offset) % 40;
        const rScale = 1 + localFrame / 40 * 0.6;
        const rOpacity = (1 - localFrame / 40) * 0.15;
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              width: 400,
              height: 400,
              borderRadius: '50%',
              border: `2px solid ${EMOTIONAL_COLORS.primary.rose}`,
              opacity: rOpacity,
              transform: `scale(${rScale})`,
            }}
          />
        );
      })}

      {/* 鼓励文字 */}
      <div
        style={{
          fontSize: 76,
          fontWeight: 800,
          color: EMOTIONAL_COLORS.white,
          textAlign: 'center',
          transform: `scale(${scale * heartbeat})`,
          textShadow: '0 6px 32px rgba(0,0,0,0.35)',
          marginBottom: 56,
          letterSpacing: 2,
        }}
      >
        {encouragement}
      </div>

      {/* CTA 按钮 */}
      <div
        style={{
          background: EMOTIONAL_COLORS.gradients.warmOverlay,
          padding: '24px 64px',
          borderRadius: 60,
          fontSize: 44,
          fontWeight: 800,
          color: EMOTIONAL_COLORS.white,
          opacity: progress,
          transform: `scale(${interpolate(progress, [0, 1], [0.7, 1])})`,
          boxShadow: '0 8px 40px rgba(244, 114, 182, 0.4), inset 0 0 16px rgba(255,255,255,0.15)',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          letterSpacing: 1,
        }}
      >
        <span>❤️</span>
        <span>{ctaText}</span>
      </div>

      {/* 浮动装饰 */}
      <div
        style={{
          position: 'absolute',
          bottom: 180,
          display: 'flex',
          gap: 24,
        }}
      >
        {['💪', '❤️', '✨'].map((emoji, i) => {
          const emojiProgress = spring({
            frame: frame - (25 + i * 10),
            fps,
            config: { damping: 18, stiffness: 110 },
          });
          const float = Math.sin(frame * 0.1 + i * 2.5) * 10;
          return (
            <div
              key={i}
              style={{
                fontSize: 48,
                opacity: emojiProgress,
                transform: `translateY(${interpolate(emojiProgress, [0, 1], [30, 0]) + float}px)`,
              }}
            >
              {emoji}
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

// ============================================
// 🎬 完整情感故事模板
// ============================================
export const XHSEmotionalStory: React.FC<{
  question: string;
  questionSubtext?: string;
  stories: Array<{
    number: string;
    title: string;
    content: string;
    emoji?: string;
  }>;
  encouragement: string;
  ctaText: string;
  sceneTitle?: string;
}> = ({ question, questionSubtext, stories, encouragement, ctaText, sceneTitle }) => {
  const { fps } = useVideoConfig();

  return (
    <AbsoluteFill>
      <WarmGlowBackground count={35} />

      <Sequence from={0} durationInFrames={fps * 3}>
        <EmotionalHookScene question={question} subtext={questionSubtext} />
      </Sequence>

      <Sequence from={fps * 3} durationInFrames={fps * 15}>
        <StoryScene stories={stories} sceneTitle={sceneTitle} />
      </Sequence>

      <Sequence from={fps * 18} durationInFrames={fps * 2}>
        <EmotionalCTAScene encouragement={encouragement} ctaText={ctaText} />
      </Sequence>
    </AbsoluteFill>
  );
};
